import { css } from "@emotion/react";
import Markdown from "markdown-to-jsx";
import React from "react";

import { constructCloudinaryUrl } from "@every.org/common/src/helpers/cloudinary";

import { Link } from "src/components/Link";
import { VideoEmbed } from "src/components/VideoEmbed";
import { LinkAppearance } from "src/styles/link";
import { spacing } from "src/theme/spacing";
import { FontWeight } from "src/theme/text";
import { logger } from "src/utility/logger";

const CLOUDINARY_IMAGE_MARKDOWN_REGEXP =
  /^!\[([^\]]*)\]\(cloudinary:([^)]+)\)$/;
const YOUTUBE_MARKDOWN_REGEXP = /^@\[youtube\]\(([^)]+)\)$/;

const markdownMediaBlockCss = css`
  margin: ${spacing.m} 0;
  border-radius: 8px;
  overflow: hidden;
`;

const markdownImageCss = css`
  display: block;
  width: 100%;
  object-fit: cover;
`;

/**
 * Applies our standard styling to links.
 */
function MarkdownLink(props: { href: string; children: string[] }) {
  const { children, href } = props;
  if (children.length === 0) {
    logger.warn({
      message: "Invalid value passed to Markdown Link",
      data: { children },
    });
    return <React.Fragment>{children}</React.Fragment>;
  }

  return (
    <Link
      to={href}
      css={css`
        display: inline;
        font-weight: ${FontWeight.REGULAR};
      `}
      target="_blank"
      data-tname="MarkdownLink"
      appearance={LinkAppearance.HYPERLINK}
    >
      {children[0]}
    </Link>
  );
}

/**
 * Creates a line break for newlines in text unlike standard markdown which
 * does not break on newlines.
 */
function MarkdownParagaph({ children }: { children: React.ReactNode[] }) {
  return (
    <p>
      {children.map((child, index) => {
        if (typeof child === "string") {
          const lines = child.split("\n");
          return lines.map((line, index) => (
            <React.Fragment key={`line-${index}`}>
              {line}
              {index !== lines.length - 1 ? <br /> : undefined}
            </React.Fragment>
          ));
        }
        return <React.Fragment key={`child-${index}`}>{child}</React.Fragment>;
      })}
    </p>
  );
}

function MarkdownImage({
  altText,
  cloudinaryId,
}: {
  altText: string;
  cloudinaryId: string;
}) {
  return (
    <figure css={markdownMediaBlockCss}>
      <img
        css={markdownImageCss}
        src={constructCloudinaryUrl({
          cloudinaryId,
          dimensions: { width: 900 },
        })}
        alt={altText}
      />
    </figure>
  );
}

function MarkdownYouTubeVideo({ videoUrl }: { videoUrl: string }) {
  return (
    <div css={markdownMediaBlockCss}>
      <VideoEmbed
        videoUrl={videoUrl}
        borderRadius="8px"
        containerCss={{ aspectRatio: "16/9" }}
      />
    </div>
  );
}

const DEFAULT_ALLOWED_TAGS = [
  "strong",
  "em",
  "del",
  "ul",
  "ol",
  "li",
  "h1",
  "h2",
  "h3",
];

/**
 * A component that enables a limited subset of markdown styling.
 */
export function MarkdownText({
  content,
  allowedTags,
  hideMarkdown = false,
  hideMedia = false,
}: {
  content: string;
  allowedTags?: string[];
  hideMarkdown?: boolean;
  /** Excludes standalone image and video blocks from text-only previews. */
  hideMedia?: boolean;
}) {
  // Limits the types of formatting we'll apply
  // https://github.com/probablyup/markdown-to-jsx/issues/274#issuecomment-572978481
  function createElement(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    type: any,
    props?: React.Attributes | null,
    children?: React.ReactNode
  ) {
    if (hideMarkdown) {
      return React.createElement(
        React.Fragment,
        { key: props?.key },
        <React.Fragment>{children} </React.Fragment>
      );
    }

    // Custom components like MarkdownLink and MarkdownParagraph will not have
    // type String
    return typeof type !== "string" ||
      (allowedTags || DEFAULT_ALLOWED_TAGS).includes(type)
      ? React.createElement(type, props, children)
      : React.createElement(React.Fragment, { key: props?.key }, children);
  }

  // there is markdown-to-jsx package issue https://github.com/quantizor/markdown-to-jsx/issues/368
  // to fix this issue we need to replace all '-' with '\-' in the content as in content.replace(/(?<!^)-/gm, "\\-")
  // but we have not seen any issues in production caused by this bug, and trying to fix it ourselves
  // seems a bit risky. Cannot use the below code because https://caniuse.com/?search=lookbehind shows
  // that is does not work on iOS 16.3 and earlier.
  // Our issue: https://github.com/everydotorg/every.org/issues/16080
  // content = content.replace(/(?<!^)-/gm, "\\-");

  function renderMarkdown(markdownContent: string, key?: React.Key) {
    return (
      <Markdown
        key={key}
        options={{
          disableParsingRawHTML: true,
          overrides: hideMarkdown
            ? undefined
            : {
                a: MarkdownLink,
                p: MarkdownParagaph,
              },
          createElement,
        }}
      >
        {markdownContent}
      </Markdown>
    );
  }

  if (!hideMarkdown) {
    const renderedContent: React.ReactNode[] = [];
    const markdownLines: string[] = [];
    let hasMedia = false;

    const flushMarkdownLines = () => {
      if (markdownLines.length === 0) {
        return;
      }
      renderedContent.push(
        renderMarkdown(
          markdownLines.join("\n"),
          `markdown-${renderedContent.length}`
        )
      );
      markdownLines.length = 0;
    };

    content.split("\n").forEach((line) => {
      const trimmedLine = line.trim();
      const imageMatch = trimmedLine.match(CLOUDINARY_IMAGE_MARKDOWN_REGEXP);
      if (imageMatch) {
        hasMedia = true;
        if (hideMedia) {
          return;
        }
        flushMarkdownLines();
        renderedContent.push(
          <MarkdownImage
            key={`image-${renderedContent.length}`}
            altText={imageMatch[1].replace(/\\]/g, "]")}
            cloudinaryId={imageMatch[2]}
          />
        );
        return;
      }

      const youtubeMatch = trimmedLine.match(YOUTUBE_MARKDOWN_REGEXP);
      if (youtubeMatch) {
        hasMedia = true;
        if (hideMedia) {
          return;
        }
        flushMarkdownLines();
        renderedContent.push(
          <MarkdownYouTubeVideo
            key={`youtube-${renderedContent.length}`}
            videoUrl={youtubeMatch[1]}
          />
        );
        return;
      }

      markdownLines.push(line);
    });
    flushMarkdownLines();

    if (hasMedia) {
      return <React.Fragment>{renderedContent}</React.Fragment>;
    }
  }

  return renderMarkdown(content);
}
